import { DataContainer } from "@app/classes/infrastructure/datacontainer";
import { DataContainerService } from "@app/classes/infrastructure/datacontainer.service";
import { RequestTypes } from "@app/classes/infrastructure/enums";
import { IPersistable } from "@app/classes/infrastructure/IPersistable";
import { PayloadPacketFacade } from "@app/classes/infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "@app/classes/infrastructure/transportdata";
import { ValidationResultAccumulator } from "@app/classes/infrastructure/validationresultaccumulator";
import { IdProvider } from "@app/services/idprovider.service";
import { ServerCommunicatorService } from "@app/services/server-communicator.service";
import { UIUtils } from "@app/services/uiutils.service";
import { Utils } from "@app/services/utils.service";
import { isNullOrUndefined } from "src/tools";
import { WorkGalleryFetchRequest } from "./workgalleryfetchrequest";

export class WorkGalleryProps {

  public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public description_mr: string = '';
  public description_en: string = '';
  public image_path: string = '';
  public image_blob_url: string = '';

  public readonly is_newly_created: boolean = false;


  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new WorkGalleryProps(true);
  }
}

export class WorkGallery implements IPersistable<WorkGallery> {
  public static readonly mastertablename: string = 'work_gallery_master';

  private constructor(public readonly p: WorkGalleryProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): WorkGallery {
    let newWorkGallery: WorkGalleryProps = Utils.GetInstance().DeepCopy(this.p);
    return WorkGallery.CreateInstance(newWorkGallery, true);
  }

  public static CreateNewInstance() {
    return new WorkGallery(WorkGalleryProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new WorkGallery(data as WorkGalleryProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
    if (!this.p.description_en || this.p.description_en.trim() == '') {
      vra.add('description_en', 'Description cannot be blank. ');
    }
    if (!this.p.description_mr || this.p.description_mr.trim() == '') {
      vra.add('description_mr', 'Description cannot be blank. ');
    }
    // if (this.p.WorkGalleryRef <= 0) vra.add('WorkGalleryRef', 'WorkGallery cannot be blank. ');
    // if (this.p.CityRef <= 0) vra.add('CityRef', 'City cannot be blank. ');
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, WorkGallery.mastertablename, this.p);
  }

  private static m_currentInstance: WorkGallery = WorkGallery.CreateNewInstance();

  public static GetCurrentInstance() {
    return WorkGallery.m_currentInstance;
  }

  public static SetCurrentInstance(value: WorkGallery) {
    WorkGallery.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): WorkGallery {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, WorkGallery.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, WorkGallery.mastertablename)!.Entries) {
        return WorkGallery.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): WorkGallery[] {
    let result: WorkGallery[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, WorkGallery.mastertablename)) {
      let coll = dcs.GetCollection(cont, WorkGallery.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(WorkGallery.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): WorkGallery[] {
    return WorkGallery.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: WorkGalleryFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchTransportDataWithoutLoginToken(req: WorkGalleryFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequestWithoutLoginToken(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  // Function created For Trasaction Service filters 
  public static async FetchTransportDataForTransactionFilter(req: WorkGalleryFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new WorkGalleryFetchRequest();
    req.work_gallery_ids.push(id);

    let tdResponse = await WorkGallery.FetchTransportData(req, errorHandler) as TransportData;
    return WorkGallery.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: WorkGalleryFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await WorkGallery.FetchTransportData(req, errorHandler) as TransportData;
    return WorkGallery.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new WorkGalleryFetchRequest();
    let tdResponse = await WorkGallery.FetchTransportData(req, errorHandler) as TransportData;
    return WorkGallery.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListWithoutLoginToken(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new WorkGalleryFetchRequest();
    let tdResponse = await WorkGallery.FetchTransportDataWithoutLoginToken(req, errorHandler) as TransportData;
    return WorkGallery.ListFromTransportData(tdResponse);
  }


  public static async FetchEntireListByStateRef(state_id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new WorkGalleryFetchRequest();
    req.state_ids.push(state_id)
    let tdResponse = await WorkGallery.FetchTransportData(req, errorHandler) as TransportData;
    return WorkGallery.ListFromTransportData(tdResponse);
  }

  // get all asha for Trasaction filter service file 
  public static async FetchEntireListForTransactionFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new WorkGalleryFetchRequest();
    let tdResponse = await WorkGallery.FetchTransportDataForTransactionFilter(req, errorHandler) as TransportData;
    return WorkGallery.ListFromTransportData(tdResponse);
  }


  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
